import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  SunIcon,
  MoonIcon,
  UserCircleIcon,
  CreditCardIcon,
  XMarkIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import GscPropertySelector from './GscPropertySelector';
import { NAVIGATION_CONFIG } from './SidebarConfig';
import Logo from '../common/Logo';
import VerifyPillButton from '../ui/VerifyPillButton';
import { FaPlus } from 'react-icons/fa';
import { isAnimationEnabled, toggleAnimation } from '../../utils/animationPreferences';

// Style utility functions
const createThemeClasses = (darkMode, lightClasses, darkClasses) => 
  darkMode ? darkClasses : lightClasses;

const getHoverClasses = (darkMode) => 
  createThemeClasses(darkMode, 'hover:bg-gray-100', 'hover:bg-gray-800');

const getActiveClasses = (darkMode) => 
  createThemeClasses(darkMode, 'bg-blue-50 text-blue-700', 'bg-blue-900/50 text-blue-300');

const getTextClasses = (darkMode) => 
  createThemeClasses(darkMode, 'text-gray-700', 'text-gray-300');

const getBorderClasses = (darkMode) => 
  createThemeClasses(darkMode, 'border-gray-200', 'border-gray-800');

// Reusable components
const NavItem = ({ item, darkMode, onClose, isDisabled, onSpecialAction }) => {
  const Icon = item.icon;
  
  const menuItemClass = ({ isActive }) => `
    flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors
    ${isDisabled 
      ? 'opacity-50 cursor-not-allowed text-gray-400' 
      : isActive 
        ? getActiveClasses(darkMode)
        : `${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`
    }
  `;

  // Check if this is a special action item (like AI Playground)
  const isSpecialAction = item.id === 'ai-playground';
  
  // Check if this is an external link
  const isExternalLink = item.external || item.path.startsWith('http') || item.path.startsWith('mailto:');
  
  // Prevent navigation if disabled
  const handleClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
      return false;
    }

    // Handle special actions (like AI Playground portal launch)
    if (isSpecialAction && onSpecialAction) {
      e.preventDefault();
      onSpecialAction(item.id);
      onClose();
      return false;
    }
    
    // For external links, let the browser handle them naturally
    if (isExternalLink) {
      onClose();
      return true;
    }
    
    // Scroll to top of main content area for internal links
    const mainScrollContainer = document.getElementById('main-scroll-container');
    if (mainScrollContainer) {
      mainScrollContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    onClose();
  };

  // For special actions, render as button instead of NavLink
  if (isSpecialAction) {
    return (
      <button
        key={item.id}
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          w-full text-left flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors
          ${isDisabled 
            ? 'opacity-50 cursor-not-allowed text-gray-400' 
            : `${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`
          }
        `}
      >
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
        {isDisabled && (
          <span className="ml-auto text-xs text-gray-400">
            Analysis running...
          </span>
        )}
      </button>
    );
  }

  // For external links, render as regular anchor tag
  if (isExternalLink) {
    return (
      <a
        key={item.id}
        href={item.path}
        target={item.path.startsWith('mailto:') ? '_self' : '_blank'}
        rel={item.path.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
        className={`
          w-full text-left flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors
          ${isDisabled 
            ? 'opacity-50 cursor-not-allowed text-gray-400' 
            : `${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`
          }
        `}
        onClick={handleClick}
      >
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
        {item.badge && (
          <span className="ml-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            {item.badge}
          </span>
        )}
        {isDisabled && (
          <span className="ml-auto text-xs text-gray-400">
            Analysis running...
          </span>
        )}
      </a>
    );
  }

  return (
    <NavLink
      key={item.id}
      to={item.path}
      className={menuItemClass}
      end={item.exact}
      onClick={handleClick}
    >
      <Icon className="w-5 h-5" />
      <span>{item.label}</span>
      {item.badge && (
        <span className="ml-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {item.badge}
        </span>
      )}
      {isDisabled && (
        <span className="ml-auto text-xs text-gray-400">
          Analysis running...
        </span>
      )}
    </NavLink>
  );
};

const NavSection = ({ section, isExpanded, onToggle, darkMode, onClose, isItemDisabled, onSpecialAction }) => {
  const sectionHeaderClass = `flex items-center justify-between p-3 cursor-pointer ${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`;

  return (
    <div className="mt-4">
      <div className={sectionHeaderClass} onClick={() => onToggle(section.key)}>
        <span className="font-medium">{section.title}</span>
        {isExpanded ? 
          <ChevronDownIcon className="w-5 h-5" /> : 
          <ChevronRightIcon className="w-5 h-5" />
        }
      </div>
      
      {isExpanded && (
        <div className="mt-1 space-y-1 pl-2">
          {section.items.map(item => (
            <NavItem 
              key={item.id} 
              item={item} 
              darkMode={darkMode} 
              onClose={onClose}
              isDisabled={isItemDisabled ? isItemDisabled(item) : false}
              onSpecialAction={onSpecialAction}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MobileAccountActions = ({ darkMode, onClose, handleLogout }) => (
  <div className="md:hidden mb-4 space-y-2">
    <NavLink
      to="/subscription"
      onClick={onClose}
      className={`flex items-center w-full px-3 py-2 rounded-md ${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`}
    >
      <CreditCardIcon className="h-5 w-5 mr-3" />
      <span>Subscription</span>
    </NavLink>

    <button
      onClick={handleLogout}
      className={`flex items-center w-full px-3 py-2 rounded-md ${getHoverClasses(darkMode)} ${
        darkMode ? 'text-red-400' : 'text-red-600'
      }`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
      <span>Sign out</span>
    </button>
  </div>
);

const AccountDropdown = ({ authStatus, darkMode, handleLogout, setIsAccountMenuOpen }) => (
  <div className={`
    absolute bottom-full left-0 mb-1
    md:bottom-full md:top-auto
    sm:top-full sm:bottom-auto sm:mt-1 sm:mb-0
    w-full rounded-md shadow-lg z-50
    ${createThemeClasses(darkMode, 'bg-white border border-gray-200', 'bg-gray-800 border border-gray-700')}
  `}>
    <div className={`p-3 text-sm ${getTextClasses(darkMode)}`}>
      {authStatus?.isChecking ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
          <span>Checking authentication...</span>
        </div>
      ) : authStatus?.isAuthenticated ? (
        <div>
          <NavLink
            to="/subscription"
            onClick={() => setIsAccountMenuOpen(false)}
            className={`block w-full text-left px-3 py-2 rounded-md text-sm mb-2 ${
              createThemeClasses(darkMode, 'hover:bg-gray-100 text-gray-700', 'hover:bg-gray-700 text-gray-300')
            }`}
          >
            <CreditCardIcon className="w-4 h-4 inline mr-2" />
            Subscription
          </NavLink>
          
          <div className={`my-2 border-t ${getBorderClasses(darkMode)}`}></div>
          
          <button
            onClick={handleLogout}
            className={`w-full text-left px-3 py-2 rounded-md text-sm ${
              createThemeClasses(darkMode, 'hover:bg-gray-100 text-red-600', 'hover:bg-gray-700 text-red-400')
            }`}
          >
            Sign out
          </button>
        </div>
      ) : (
        <NavLink
          to="/login"
          onClick={() => setIsAccountMenuOpen(false)}
          className={`block w-full text-center px-3 py-2 rounded-md text-sm ${
            createThemeClasses(darkMode, 'bg-blue-500 hover:bg-blue-600 text-white', 'bg-blue-600 hover:bg-blue-700 text-white')
          }`}
        >
          Sign in
        </NavLink>
      )}
    </div>
  </div>
);

const Sidebar = ({ 
  isOpen,
  onClose,
  darkMode,
  toggleDarkMode,
  fullWidth,
  toggleFullWidth,
  authStatus,
  handleLogout,
  getGravatarUrl,
  gscData,
  selectedProperty,
  onPropertyChange,
  progressiveAnalysisState,
  isAnalysisRunning,
  topicAnalysisState,
  onAIPlaygroundOpen,
  onPropertiesRefresh
}) => {
  const [expandedSections, setExpandedSections] = useState({
    analysis: true,
    ai_tools: true,  // Expand AI Tools by default
    recommendations: true,
    reports: true,
    tools: true,
    demo: true,
    performance: true,
  });
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [forceEnable, setForceEnable] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(() => isAnimationEnabled('sparkleTrails'));
  const accountMenuRef = useRef(null);



  // Determine if navigation should be disabled during progressive analysis
  const isProgressiveAnalysisActive = isAnalysisRunning && (
    progressiveAnalysisState?.stage === 'polling' || 
    progressiveAnalysisState?.stage === 'summary_complete'
  );

  // Determine if navigation should be disabled during topic analysis
  const isTopicAnalysisActive = topicAnalysisState?.isRunning && 
    topicAnalysisState?.stage === 'running';

  // 30-second safety timeout to prevent permanent lockout
  useEffect(() => {
    if (isProgressiveAnalysisActive || isTopicAnalysisActive) {
      setForceEnable(false);
      const timeout = setTimeout(() => {
        setForceEnable(true);
      }, 30000); // 30 seconds
      
      return () => clearTimeout(timeout);
    } else {
      setForceEnable(false);
    }
  }, [isProgressiveAnalysisActive, isTopicAnalysisActive]);

  const shouldDisableNavigation = (isProgressiveAnalysisActive || isTopicAnalysisActive) && !forceEnable;

  // Analysis-related paths that should be disabled during progressive analysis
  const analysisRelatedPaths = ['/keywords', '/topics', '/overlap', '/pages', '/overview', '/analysis'];

  const isItemDisabled = (item) => {
    return shouldDisableNavigation && analysisRelatedPaths.some(path => 
      item.path.startsWith(path)
    );
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePropertySelect = (property) => {
    if (onPropertyChange) {
      onPropertyChange(property);
    }
  };

  const handleToggleAnimations = () => {
    const newState = toggleAnimation('sparkleTrails');
    setAnimationsEnabled(newState);
  };

  // Handle special navigation actions
  const handleSpecialAction = (actionId) => {
    if (actionId === 'ai-playground' && onAIPlaygroundOpen) {
      onAIPlaygroundOpen();
    }
  };

  const menuItemClass = ({ isActive }) => `
    flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors
    ${isActive 
      ? getActiveClasses(darkMode)
      : `${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`
    }
  `;

  return (
    <div className={`
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      transform transition-transform duration-300 ease-in-out
      md:translate-x-0
      w-64 h-screen overflow-y-auto fixed left-0 top-0 z-40 flex flex-col
      ${createThemeClasses(darkMode, 'bg-white border-r border-gray-200', 'bg-gray-900 border-r border-gray-800')}
    `}>
      {/* Mobile close button */}
      <div className="md:hidden absolute right-4 top-4">
        <button
          onClick={onClose}
          className={`p-2 rounded-md ${getHoverClasses(darkMode)}`}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Logo */}
      <div className="p-4">
        <Logo 
          darkMode={darkMode}
          size="md"
          linkTo="/"
        />
      </div>

      {/* Main navigation */}
      <div className="flex-1 overflow-y-auto scrollbar-modern">
        <div className="md:mt-2 mt-16">
          {/* Home - always visible */}
          <NavLink to="/dashboard" className={menuItemClass} end>
            <HomeIcon className="w-5 h-5" />
            <span>Dashboard</span>
          </NavLink>

          {/* New Analysis - always visible */}
          <NavLink to="/new" className={menuItemClass} end>
            <ArrowPathIcon className="w-5 h-5" />
            <span>New Analysis</span>
          </NavLink>

          {/* GSC Property Selector */} 
          {authStatus?.isAuthenticated && gscData?.properties && (
            <div className={`mt-3 mb-3 px-2 py-2 border-y ${getBorderClasses(darkMode)}`}>
              <div className="mt-2 px-2 space-y-1">
                <GscPropertySelector 
                  gscProperties={gscData.properties}
                  currentGscProperty={selectedProperty}
                  onGscPropertySelect={handlePropertySelect}
                  darkMode={darkMode}
                  onPropertiesRefresh={onPropertiesRefresh}
                />
              </div>
            </div>
          )}

          {/* Analysis Status */}
          {shouldDisableNavigation && (
            <div className={`mx-4 mb-3 p-3 rounded-md ${
              createThemeClasses(darkMode, 'bg-blue-50 border border-blue-200', 'bg-blue-900/30 border border-blue-700')
            }`}>
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                <span className={`text-sm font-medium ${createThemeClasses(darkMode, 'text-blue-700', 'text-blue-300')}`}>
                  {isTopicAnalysisActive ? 'Topic analysis in progress...' : 'Analysis in progress...'}
                </span>
              </div>
              <p className={`text-xs mt-1 ${createThemeClasses(darkMode, 'text-blue-600', 'text-blue-400')}`}>
                {isTopicAnalysisActive 
                  ? topicAnalysisState?.message || 'Processing topic clustering...'
                  : progressiveAnalysisState?.message || 'Processing your data...'
                }
              </p>
              <p className={`text-xs mt-1 ${createThemeClasses(darkMode, 'text-blue-500', 'text-blue-500')}`}>
                Analysis sections will be available when complete
              </p>
            </div>
          )}

          {/* Verify Tool Button */}
          {authStatus?.isAuthenticated && (
            <div className="mx-4 mb-4">
              <VerifyPillButton darkMode={darkMode} />
            </div>
          )}

          {/* Navigation sections */}
          {authStatus?.isAuthenticated ? (
            <>
              {Object.values(NAVIGATION_CONFIG).map(section => (
                <NavSection
                  key={section.key}
                  section={section}
                  isExpanded={expandedSections[section.key]}
                  onToggle={toggleSection}
                  darkMode={darkMode}
                  onClose={onClose}
                  isItemDisabled={isItemDisabled}
                  onSpecialAction={handleSpecialAction}
                />
              ))}
            </>
          ) : (
            // Non-authenticated user message
            <div className={`mt-6 px-4 py-3 rounded-md ${
              createThemeClasses(darkMode, 'bg-gray-100 text-gray-600', 'bg-gray-800 text-gray-300')
            }`}>
              <p className="text-sm">
                Please sign in to access reports and tools.
              </p>
              <NavLink 
                to="/login" 
                className={({ isActive }) =>
                  `mt-2 inline-block px-4 py-2 rounded-md text-sm font-medium ${
                    createThemeClasses(darkMode, 'bg-blue-500 hover:bg-blue-600 text-white', 'bg-blue-600 hover:bg-blue-700 text-white')
                  }`
                }
              >
                Sign In
              </NavLink>
            </div>
          )}
        </div>
      </div>

      {/* Bottom section */}
      <div className={`p-4 ${authStatus?.isAuthenticated ? 'md:pb-4 pb-16' : 'pb-4'} border-t ${getBorderClasses(darkMode)}`}>
        {/* Layout toggles */}
        <div className="flex gap-2 mb-3">
          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            className={`flex items-center justify-center flex-1 px-2 py-2 rounded-md ${getHoverClasses(darkMode)} ${
              darkMode ? 'text-yellow-300' : getTextClasses(darkMode)
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? (
              <SunIcon className="w-5 h-5" />
            ) : (
              <MoonIcon className="w-5 h-5" />
            )}
          </button>

          {/* Full width toggle */}
          <button
            onClick={toggleFullWidth}
            className={`flex items-center justify-center flex-1 px-2 py-2 rounded-md ${getHoverClasses(darkMode)} ${getTextClasses(darkMode)}`}
            title={fullWidth ? 'Switch to Compact View' : 'Switch to Full Width'}
          >
            {fullWidth ? (
              <ArrowsPointingInIcon className="w-5 h-5" />
            ) : (
              <ArrowsPointingOutIcon className="w-5 h-5" />
            )}
          </button>

          {/* Animation toggle */}
          <button
            onClick={handleToggleAnimations}
            className={`flex items-center justify-center flex-1 px-2 py-2 rounded-md ${getHoverClasses(darkMode)} ${
              animationsEnabled ? 'text-yellow-400' : getTextClasses(darkMode)
            }`}
            title={animationsEnabled ? 'Disable Animations' : 'Enable Animations'}
          >
            <SparklesIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile account actions */}
        {authStatus?.isAuthenticated && (
          <MobileAccountActions 
            darkMode={darkMode} 
            onClose={onClose} 
            handleLogout={handleLogout} 
          />
        )}

        {/* Desktop user account section */}
        <div className="relative sm:mb-12 md:mb-0 hidden md:block" ref={accountMenuRef}>
          <button
            onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
            className={`flex items-center w-full px-3 py-2 rounded-md ${getTextClasses(darkMode)} ${getHoverClasses(darkMode)}`}
          >
            {authStatus?.user?.email ? (
              <>
                <img 
                  src={getGravatarUrl(authStatus.user.email)} 
                  alt="User avatar" 
                  className="w-6 h-6 rounded-full mr-3"
                />
                <div className="text-left overflow-hidden">
                  <div className="truncate text-sm">{authStatus.user.email}</div>
                </div>
              </>
            ) : (
              <>
                <UserCircleIcon className="w-5 h-5 mr-3" />
                <span>Sign In</span>
              </>
            )}
          </button>

          {/* Account dropdown */}
          {isAccountMenuOpen && (
            <AccountDropdown 
              authStatus={authStatus}
              darkMode={darkMode}
              handleLogout={handleLogout}
              setIsAccountMenuOpen={setIsAccountMenuOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Sidebar); 